import { create } from 'zustand';
import api from './api';

export const useAuthStore = create((set) => ({
  user: null,
  isAuthenticated: false,
  isLoading: true,
  login: async (username, password) => {
    const { user } = await api.login(username, password);
    set({ user, isAuthenticated: true });
    return user;
  },
  logout: () => { api.setToken(null); set({ user: null, isAuthenticated: false }); },
  checkAuth: async () => {
    try {
      const token = api.getToken();
      if (!token) { set({ isLoading: false }); return; }
      const { user } = await api.getMe();
      set({ user, isAuthenticated: true, isLoading: false });
    } catch { api.setToken(null); set({ isLoading: false }); }
  }
}));

export const useNotesStore = create((set, get) => ({
  notes: [], currentNote: null, backlinks: [], outlinks: [], wikiLinks: [], tags: [], folders: [],
  recentNotes: [], templates: [], trashNotes: [], isLoading: false, searchQuery: '', searchResults: [],
  currentView: 'notes', // notes, table, calendar, timeline, reading

  fetchNotes: async (params) => { set({ isLoading: true }); const { notes } = await api.listNotes(params); set({ notes, isLoading: false }); },
  fetchNote: async (id) => { set({ isLoading: true }); try {
    const { note, backlinks, outlinks, wikiLinks } = await api.getNote(id);
    set({ currentNote: note, backlinks: backlinks || [], outlinks: outlinks || [], wikiLinks: wikiLinks || [], isLoading: false });
    return note;
  } catch (e) { set({ isLoading: false, currentNote: null }); return null; } },
  createNote: async (data) => { const { note } = await api.createNote(data); set(s => ({ notes: [note, ...s.notes] })); return note; },
  updateNote: async (id, data) => { const { note } = await api.updateNote(id, data); set(s => ({ notes: s.notes.map(n => n.id === id ? note : n), currentNote: s.currentNote?.id === id ? note : s.currentNote })); return note; },
  deleteNote: async (id) => { await api.deleteNote(id); set(s => ({ notes: s.notes.filter(n => n.id !== id), currentNote: s.currentNote?.id === id ? null : s.currentNote })); },
  toggleStar: async (id) => { const { starred } = await api.toggleStar(id); set(s => ({ notes: s.notes.map(n => n.id === id ? { ...n, starred } : n) })); },
  shareNote: async (id) => { return api.shareNote(id); },
  fetchTags: async () => { const { tags } = await api.getTags(); set({ tags }); },
  fetchFolders: async () => { const { folders } = await api.getFolders(); set({ folders }); },
  fetchRecent: async () => { const { notes } = await api.getRecent(); set({ recentNotes: notes }); },
  fetchTemplates: async () => { const { templates } = await api.getTemplates(); set({ templates }); },
  createTemplate: async (data) => { await api.createTemplate(data); await get().fetchTemplates(); },
  deleteTemplate: async (id) => { await api.deleteTemplate(id); await get().fetchTemplates(); },
  fetchTrash: async () => { const { notes } = await api.getTrash(); set({ trashNotes: notes }); },
  restoreFromTrash: async (id) => { await api.restoreFromTrash(id); await get().fetchTrash(); },
  permanentDelete: async (id) => { await api.permanentDelete(id); await get().fetchTrash(); },
  emptyTrash: async () => { await api.emptyTrash(); set({ trashNotes: [] }); },
  search: async (q) => { set({ searchQuery: q }); if (!q) { set({ searchResults: [] }); return; } const { results } = await api.search(q); set({ searchResults: results }); },
  clearSearch: () => set({ searchQuery: '', searchResults: [] }),
  setView: (v) => set({ currentView: v })
}));

export const useUIStore = create((set) => ({
  sidebarOpen: true, editorMode: 'split', aiPanelOpen: false, commandPaletteOpen: false,
  toasts: [],
  toggleSidebar: () => set(s => ({ sidebarOpen: !s.sidebarOpen })),
  setEditorMode: (m) => set({ editorMode: m }),
  toggleAiPanel: () => set(s => ({ aiPanelOpen: !s.aiPanelOpen })),
  toggleCommandPalette: () => set(s => ({ commandPaletteOpen: !s.commandPaletteOpen })),
  addToast: (toast) => set(s => ({ toasts: [...s.toasts, { id: Date.now(), ...toast }] })),
  removeToast: (id) => set(s => ({ toasts: s.toasts.filter(t => t.id !== id) }))
}));
